import type { RefObject } from "react";
import { useLocale } from "../providers/LocaleProvider";
import { LoadingIndicator } from "./LoadingIndicator";

type AppStatus = "idle" | "processing" | "success" | "empty" | "error";

export interface DropzoneProps {
  /** Current application status */
  status: AppStatus;
  /** Whether the user is currently dragging a file over the zone */
  isDragging: boolean;
  /** File name when processing, success, empty, or error */
  fileName?: string;
  /** Error message when status is "error" */
  errorMessage?: string;
  /** Ref to the hidden file input */
  fileInputRef: RefObject<HTMLInputElement | null>;
  /** Called when a file is selected or dropped */
  onFile: (file: File) => void;
  /** Called when drag enters the zone */
  onDragOver: (event: React.DragEvent) => void;
  /** Called when drag leaves the zone */
  onDragLeave: () => void;
  /** Called when a file is dropped */
  onDrop: (event: React.DragEvent) => void;
}

/**
 * File drop area component. Handles drag/drop interactions and displays
 * different states: idle, dragging, loading, success, empty, and error.
 * All text strings use `t()` from the locale provider.
 */
export function Dropzone({
  status,
  isDragging,
  fileName,
  fileInputRef,
  onFile,
  onDragOver,
  onDragLeave,
  onDrop,
}: DropzoneProps) {
  const { t } = useLocale();

  const classNames = [
    "dropzone",
    isDragging ? "dragging" : "",
    status === "processing" ? "loading" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={classNames}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-label={t("aria.dropzone")}
    >
      <input
        ref={fileInputRef}
        accept=".json,application/json"
        className="file-input"
        aria-label={t("aria.fileInput")}
        onChange={(event) => {
          const file = event.currentTarget.files?.item(0);
          if (file) onFile(file);
          event.currentTarget.value = "";
        }}
        type="file"
      />

      {status === "processing" && fileName ? (
        <LoadingIndicator fileName={fileName} />
      ) : (
        <div className="dropzone-box">
          <div className="drop-art" aria-hidden="true">
            SRT
          </div>
          <div>
            <h2>{getTitle(status, fileName, t)}</h2>
            <p>{getSubtitle(status, fileName, t)}</p>
          </div>
          <div className="action-row">
            <button
              className="primary-action"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              {t("dropzone.chooseFile")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function getTitle(
  status: AppStatus,
  fileName: string | undefined,
  t: (key: keyof import("../i18n/types").TranslationKeys) => string
): string {
  switch (status) {
    case "processing":
      return t("dropzone.processing");
    case "success":
    case "empty":
      return fileName ?? "";
    case "error":
      return t("dropzone.checkFile");
    default:
      return t("dropzone.title");
  }
}

function getSubtitle(
  status: AppStatus,
  fileName: string | undefined,
  t: (key: keyof import("../i18n/types").TranslationKeys) => string
): string {
  switch (status) {
    case "processing":
      return fileName ?? "";
    case "success":
      return t("dropzone.conversionReady");
    case "empty":
      return t("dropzone.noSubtitles");
    case "error":
      return fileName ?? "";
    default:
      return t("dropzone.subtitle");
  }
}
